import classNames from 'classnames';
import React from 'react';
import * as mobxReact from 'mobx-react';
import { IAttendeeDetailsDTO, IAttendeeDTO, MeetingAttendeeJoinMethod, MeetingAttendeeStatus } from '../../../../api/attendees.dao';
import { CircledWIcon } from '../../icons/icons';
import Text from '../../text/text';
import { AttendeeDetailsPopover } from '../attendee-column-popover/attendee-column-popover';
import CheckMarkSVG from '../../../../images/svg/icons8-checkmark';
import VideoSVG from '../../../../images/svg/icons8-video-calls';
import AudioSVG from '../../../../images/svg/icons8-audio';
import PersonSVG from '../../../../images/svg/icons8-person';
import './_attendee-info.scss';
import { OpenFrom } from '../../../../tracking-service/tracking-values';
import { AttendeeColumnPopoverViewmodel } from '../attendee-column-popover/attendee-column-popover.viewmodel';


interface IProps {
    searchValue?: string;
    attendee: IAttendeeDTO;
    handleRemoveAttendeeClick?: (attendeeId: number, organizationId: number) => void;
    withAttendeePopover?: boolean;
    withLiveStatus?: boolean;
    withRemove?: boolean;
    isMeetingUnderway?: boolean;
    organizationId?: number;
    attendeeDetailsPopoverVM?: AttendeeColumnPopoverViewmodel;
    additionalAttendeeId?: string;
    openFrom?: OpenFrom;
    onAttendeeEditSaveCallback?: (attendee: IAttendeeDetailsDTO) => void;
}

export const AttendeeInfo = mobxReact.observer((props: IProps) => {
    const {
        withAttendeePopover,
        withRemove,
        handleRemoveAttendeeClick,
        withLiveStatus,
        searchValue,
        attendee,
        isMeetingUnderway,
        organizationId,
        attendeeDetailsPopoverVM,
        additionalAttendeeId,
        openFrom,
        onAttendeeEditSaveCallback,
    } = props;
    const { attendeeId, firstName = '', lastName = '', status, issues, joinMethod } = attendee;
    const fullName = firstName + ' ' + lastName;

    if (!withAttendeePopover) {
        return (
            <Text
                tag='div'
                className='attendee-list-item__text'
                message={fullName}
                highlightValue={searchValue}
                withEllipsis
                withHighlight
                escapeHtml={true}
            />
        );
    }


    const isJoined = status === MeetingAttendeeStatus.JOINED || status === MeetingAttendeeStatus.WALK_IN;
    const shouldHighlight = isMeetingUnderway && (
        (!attendee.joinMethod && (attendee.status === MeetingAttendeeStatus.JOINED || status === MeetingAttendeeStatus.WALK_IN)) ||
        attendee.status === MeetingAttendeeStatus.CONFIRMED);

    const JoinedIcon = () => {
        if (joinMethod === MeetingAttendeeJoinMethod.AUDIO) {
            return <div className='attendee-info__icon attendee-info__join-method-icon'>
                <AudioSVG />
            </div>;
        }
        if (joinMethod === MeetingAttendeeJoinMethod.VIDEO) {
            return <div className='attendee-info__icon attendee-info__join-method-icon'>
                <VideoSVG />
            </div>;
        }
        if (joinMethod === MeetingAttendeeJoinMethod.IN_PERSON) {
            return <div className='attendee-info__icon attendee-info__join-method-icon'>
                <PersonSVG />
            </div>;
        }
        return <div className={classNames('attendee-info__icon', {
            'attendee-info__icon--not-joined': !isJoined
        })}><CheckMarkSVG />
        </div>;
    };

    const meetingAttendeeId = additionalAttendeeId + attendeeId;
    const isOpen = attendeeDetailsPopoverVM.isOpen(meetingAttendeeId);

    return (
        <AttendeeDetailsPopover
            attendeeId={attendeeId}
            padding={10}
            withRemove={withRemove}
            issues={issues}
            withLiveStatus={withLiveStatus}
            organizationId={organizationId}
            vm={attendeeDetailsPopoverVM}
            meetingAttendeeId={meetingAttendeeId}
            isOpen={isOpen}
            openFrom={openFrom}
            onAttendeeEditSaveCallback={onAttendeeEditSaveCallback}
            handleRemoveAttendeeClick={handleRemoveAttendeeClick}
        >
            {withLiveStatus && <JoinedIcon />}
            <Text
                tag='div'
                className={classNames('attendee-list-item__text', {
                    'attendee-list-item__text--with-issue':
                        withLiveStatus && (status === MeetingAttendeeStatus.NOSHOW || issues?.length),
                    'attendee-list-item__text--cancelled': withLiveStatus
                        && status === MeetingAttendeeStatus.CANCELLED,
                    'attendee-list-item__text--with-hover': withAttendeePopover,
                    'attendee-list-item__text--not-joined': withLiveStatus && !isJoined,
                    'attendee-list-item__text--data-is-incomplete': shouldHighlight
                })}
                message={fullName}
                highlightValue={searchValue}
                withEllipsis
                withHighlight
                escapeHtml={true}
            />
            {withLiveStatus && status === MeetingAttendeeStatus.WALK_IN && <CircledWIcon />}
        </AttendeeDetailsPopover>
    );
});
